/**
 * One part's decomposition - what makes the design walkable in two clicks.
 *
 * A definition lists the usages declared inside it, and each of those carries a
 * link to THE DEFINITION IT IS TYPED BY, which `contains[].definition` has
 * already resolved on the server. That resolution is the whole value of the
 * pane: `type_name` arrives written with package short names (`GNC::Guidance`),
 * so matching it in the browser would be a guess dressed as a fact. Definition →
 * usage inside it → the definition that usage is a kind of: two clicks, no guess.
 *
 * The pane always shows a DEFINITION now, which is Patrick's third observation
 * settled: attributes, masses and the decomposition all live on the definition, so
 * clicking a usage lands here and the usage contributes its MULTIPLICITY as context
 * - said at the top, as a placement, rather than being a competing destination. The
 * one exception is a usage whose type resolves to nothing: it keeps its own page,
 * because there is nowhere else to send it, and the pane says so instead of drawing
 * an empty definition.
 *
 * It also carries the ROLLED-UP quantities, and this is the one place that shows
 * a part's OWN value beside the total of its subtree - the contract's distinction
 * between what a part weighs itself and what everything inside it weighs, which is
 * the point of the feature and cannot be checked from a total alone. Which
 * quantities appear comes from `detail.attributes`, the model's own statement of
 * what aggregates, so a program that adds one appears here untouched;
 * `detail.unstorable` names the ones the model rolls up and BEST cannot store, and
 * is said rather than dropped.
 *
 * Four things are EDITABLE here, all gated on `useCanWrite()` and all disabled
 * with the reason rather than hidden:
 *
 *   - the quantities the model declares, in place, through the same quantity
 *     control the generated forms use. Only CHANGED fields are sent, because the
 *     API leaves an omitted field alone and CLEARS an empty one - so sending all
 *     three to edit one would unset the other two;
 *   - "Add child part", which now does what it says. It was disabled for want of a
 *     route and `POST /api/parts/{id}/children` is that route;
 *   - "Add port" and "Connect ports" (issue #52): a port declared inside the
 *     definition, and an interface joining two ports on the parts inside it.
 *
 * Anything beyond those is the full editor at `/parts/{id}`, which a double-click
 * on any row opens and which this pane links to by name.
 */

import { useEffect, useMemo, useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { Link as RouterLink } from 'react-router-dom'
import { ApiError, api } from '../../api'
import type { KindSchema, PartDetail, Schema } from '../../schema'
import { useToasts } from '../../state/Toasts'
import { MONOSPACE } from '../../theme'
import AddChildDialog from './AddChildDialog'
import InterfacesSection from './InterfacesSection'
import PortsSection from './PortsSection'
import AttributesTable from './AttributesTable'
import StateValuesPanel from './StateValuesPanel'
import LinkedRequirements from '../links/LinkedRequirements'
import HelpTip from '../common/HelpTip'
import { addChildRefusal, isGenericKind } from './parts-model'
import type { UsageContext, WritePermission } from './parts-model'
import { UNRESOLVED_USAGE } from './parts-model'
import {
  CLEARING_NOTE,
  ATTRIBUTE_NOTE,
  ATTRIBUTE_TABLE_LABEL,
  attributeChanges,
  attributeDrafts,
  savedSummary,
} from './parts-editing'
import type { AttributeDraft } from './parts-editing'
import type { EntityRow, StateValue } from '../../schema'
import { TotalsTable } from './PartTotals'
import { rollupColumns } from './rollup-model'

export const NOTHING_SELECTED =
  'Choose a part to see what it is made of. A definition lists the usages ' +
  'declared inside it and what each of those is a kind of; a usage names what ' +
  'contains it and what it is typed by.'

/**
 * Nothing inside, said in three words.
 *
 * It used to explain that an empty decomposition is a statement about the model
 * rather than a gap in the screen - true, and not something a reader needs told
 * every time they click a leaf part. The heading already says Child Parts and
 * carries its own '?'.
 */
export const NO_CHILDREN = 'No Child Parts'

export const OPEN_EDITOR = 'Open the full editor'

/**
 * One section heading in the pane, with its explanation behind a `?`.
 *
 * Three sections, one shape: what the part STATES, what is computed from
 * everything under it, and what is inside it. They used to be a mix of noun
 * phrases and sentence fragments with a paragraph of caption under two of them,
 * which is why a pane about one small part filled a column.
 */
function Section({ title, help, helpLabel }: {
  title: string
  help: string
  helpLabel: string
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Typography variant="subtitle2">{title}</Typography>
      <HelpTip title={help} label={helpLabel} />
    </Box>
  )
}

export const EDITOR_HINT =
  'Double-click any row to open the full editor, where every field of a part can be ' +
  'changed. What is editable here is what this pane shows.'

/** A monospaced ID that links to the element's own page, or plain text. */
function IdLink({ id }: { id: string }) {
  return (
    <RouterLink
      to={`/parts/${encodeURIComponent(id)}`}
      style={{ fontFamily: MONOSPACE, fontSize: '0.85rem' }}
    >
      {id}
    </RouterLink>
  )
}

/**
 * How a usage's multiplicity reads as context on the definition's page.
 *
 * The multiplicity is a fact about the PLACEMENT, so it is worded as one: four of
 * this definition inside that parent. Empty is the language default and says
 * nothing, which is not the same as one, so it is worded as nothing rather than
 * rendered as `[1]`.
 *
 * @param usage the usage that led here
 */
export function usageNote(usage: UsageContext): string {
  const where = usage.owner ? `inside ${usage.owner}` : 'in its package, contained by nothing'
  const many = usage.multiplicity
    ? `${usage.multiplicity} of them`
    : 'no multiplicity stated, which is the language default rather than one'
  return `Reached through the usage ${usage.name} ${where} — ${many}.`
}

export default function DecompositionPane({
  detail,
  loading,
  error,
  permission,
  schema,
  kind,
  usage = null,
  unresolved = false,
  onChanged = () => {},
}: {
  detail: PartDetail | null
  loading: boolean
  error: ApiError | null
  permission: WritePermission
  schema: Schema
  kind: KindSchema
  /** The usage that led to this definition, when a usage was clicked. */
  usage?: UsageContext | null
  /** True when this IS a usage, kept here because its type resolves to nothing. */
  unresolved?: boolean
  /** Called after a write, so the screen re-reads what it now says. */
  onChanged?: () => void
}) {
  const toasts = useToasts()

  const [drafts, setDrafts] = useState<AttributeDraft[]>([])
  const [saving, setSaving] = useState(false)
  const [failure, setFailure] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [adding, setAdding] = useState(false)

  // The state-qualified figures, and the states this part may state one in.
  // Fetched separately from the drafts above because they are a different
  // write path with different semantics - a whole list through a PUT of its
  // own - and sharing one dirty state would make "save" mean two things.
  const [stateValues, setStateValues] = useState<Record<string, StateValue[]>>({})
  const [states, setStates] = useState<EntityRow[]>([])
  const [stateReloads, setStateReloads] = useState(0)

  const partId = detail?.id
  useEffect(() => {
    if (!partId) {
      setStateValues({})
      return undefined
    }
    let live = true
    api
      .partStateValues(partId)
      .then((found) => live && setStateValues(found))
      // A program with no states is the ordinary case, not an error - the
      // panel simply says so. Failing the pane over it would take the
      // attributes down with it.
      .catch(() => live && setStateValues({}))
    return () => {
      live = false
    }
  }, [partId, stateReloads])

  useEffect(() => {
    let live = true
    api
      .list('states', { limit: 5000 })
      .then((page) => live && setStates(page.items))
      .catch(() => live && setStates([]))
    return () => {
      live = false
    }
  }, [])

  // Re-read whenever the part does. An edit in flight is abandoned on purpose:
  // the drafts belong to the part on screen, and carrying them across a selection
  // would offer to write one part's mass onto another.
  useEffect(() => {
    setDrafts(
      detail
        ? attributeDrafts(
            detail.attributes ?? [],
            detail as unknown as Record<string, unknown>,
            detail.statable,
          )
        : [],
    )
    setFailure('')
    setFieldErrors({})
    setSaving(false)
  }, [detail])

  const changes = useMemo(
    () =>
      detail ? attributeChanges(drafts, detail as unknown as Record<string, unknown>) : {},
    [drafts, detail],
  )

  if (error) return <Alert severity="error">{error.message}</Alert>
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }
  if (!detail) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '60ch' }}>
        {NOTHING_SELECTED}
      </Typography>
    )
  }

  const generic = isGenericKind(detail as unknown as Record<string, unknown>)
  const refusal = addChildRefusal(permission, detail.is_definition)
  // From the payload, in the model's declaration order. `attributes` is the
  // authority when it is there; the totals' own keys stand in if a server answers
  // the totals without the descriptors, which is a shape this screen can still
  // draw rather than one it should blank the section for.
  const columns = rollupColumns(detail.attributes, [detail])

  const save = async () => {
    if (Object.keys(changes).length === 0) return
    setSaving(true)
    setFailure('')
    setFieldErrors({})
    try {
      const answer = await api.update('parts', detail.id, changes)
      toasts.succeeded(savedSummary(answer.identifier || detail.id, answer.changed ?? {}))
      onChanged()
    } catch (thrown) {
      const failed = thrown as ApiError
      setFieldErrors(failed.fields ?? {})
      setFailure(failed.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Stack spacing={1.5}>
      <Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="subtitle1">{detail.name || detail.id}</Typography>
          <Typography variant="body2" sx={{ fontFamily: MONOSPACE }}>
            {detail.id}
          </Typography>
          <Chip
            label={detail.is_definition ? 'definition' : 'instance'}
            size="small"
            variant="outlined"
            color={detail.is_definition ? 'default' : 'secondary'}
          />
          {generic && <Chip label="generic" size="small" variant="outlined" color="warning" />}
          {/* Real hardware, marked in the model. Shown beside the totals because
              "which of these has mass at all" is the first question a budget
              raises, and software leaving its masses unset is a statement rather
              than a gap. */}
          {detail.is_physical && (
            <Chip label="physical" size="small" variant="outlined" color="success" />
          )}
          <Box sx={{ flexGrow: 1 }} />
          <RouterLink
            to={`/parts/${encodeURIComponent(detail.id)}`}
            style={{ fontSize: '0.8rem' }}
          >
            {OPEN_EDITOR}
          </RouterLink>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONOSPACE }}>
          {detail.qualified_name}
        </Typography>
      </Box>

      {/* The usage's contribution: a placement and a multiplicity, said as
          context on the definition rather than as a page of its own. */}
      {usage && !unresolved && (
        <Alert severity="info" icon={false} aria-label="How this part was reached">
          <Typography variant="body2">{usageNote(usage)}</Typography>
          <Typography variant="caption" sx={{ fontFamily: MONOSPACE, display: 'block' }}>
            {usage.name} : {usage.typeName}
            {usage.multiplicity}
          </Typography>
        </Alert>
      )}

      {unresolved && <Alert severity="warning">{UNRESOLVED_USAGE}</Alert>}

      {/* A USAGE'S TWO EDGES, and only when there is an edge to name.
          Both used to print unconditionally and both said "nothing" on nearly
          every part: `owner` and `definition` are populated on USAGES only, and
          this pane resolves every click to the DEFINITION - so a reader met two
          labelled rows saying nothing at all, on every selection.
          They still appear where they mean something: an unresolved usage keeps
          its own page and lands here, and a definition nested inside another
          part has an owner. Absent rather than empty. */}
      {(detail.owner || detail.definition) && (
        <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap' }}>
          {detail.owner && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Declared inside
              </Typography>
              <IdLink id={detail.owner} />
            </Box>
          )}
          {detail.definition && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Typed by
              </Typography>
              <IdLink id={detail.definition} />
            </Box>
          )}
        </Stack>
      )}

      {(detail.attributes ?? []).length > 0 && (
        <>
          <Divider />
          <Section
            title={ATTRIBUTE_TABLE_LABEL}
            help={`${ATTRIBUTE_NOTE} ${CLEARING_NOTE}`}
            helpLabel="How to state a quantity"
          />
          <AttributesTable
            schema={schema}
            kind={kind}
            drafts={drafts}
            isDefinition={detail.is_definition}
            permission={permission}
            onChange={(name, value) =>
              setDrafts((current) =>
                current.map((draft) => (draft.name === name ? { ...draft, value } : draft)),
              )
            }
            onSave={() => void save()}
            onRevert={() =>
              setDrafts(
                attributeDrafts(
                  detail.attributes ?? [],
                  detail as unknown as Record<string, unknown>,
                  detail.statable,
                ),
              )
            }
            dirty={Object.keys(changes).length > 0}
            saving={saving}
            failure={failure}
            fieldErrors={fieldErrors}
          />

          <Section
            title="Values that hold only in one state"
            help={
              'A part can draw one figure nominally and another in a given state - a seeker ' +
              'idle while the vehicle holds and tracking under thrust. The value in the table ' +
              'above is the BASELINE: what holds wherever nothing else is said. Unset is not ' +
              'zero, so a part powered down in a state must state an explicit 0 rather than ' +
              'leaving the row out.'
            }
            helpLabel="What a state value is"
          />
          {drafts.map((draft) => (
            <Box key={draft.name} sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2">{draft.label}</Typography>
              <StateValuesPanel
                identifier={detail.id}
                feature={draft.feature || draft.name}
                label={draft.label}
                unit={draft.unit}
                states={states}
                allowed={detail.states}
                values={stateValues[draft.feature || draft.name] ?? []}
                onSaved={() => {
                  setStateReloads((count) => count + 1)
                  onChanged?.()
                }}
              />
            </Box>
          ))}
        </>
      )}

      <Divider />

      <Section
        title="Rollup Attributes"
        help={
          'Computed from this part and everything inside it - a part\u2019s own value plus ' +
          'the total of each part it contains, times how many of each. Which quantities ' +
          'roll up is the model\u2019s statement, not this screen\u2019s.'
        }
        helpLabel="What a rollup is"
      />
      <TotalsTable
        totals={detail.totals}
        stated={detail as unknown as Record<string, unknown>}
        isDefinition={detail.is_definition}
        columns={columns}
        unstorable={detail.unstorable ?? []}
      />

      <Divider />

      {/* What this part satisfies: allocated_to, and every link a requirement
          type declares to a part (issue #48). */}
      <LinkedRequirements key={`links-${detail.id}`} kind="design_part" identifier={detail.id} />

      <Divider />

      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <Typography variant="subtitle2">Child Parts</Typography>
          <HelpTip
            label="What a child part is"
            title={
              'The usages declared inside this definition \u2014 what it is made of, and how ' +
              'many of each. A usage\u2019s multiplicity is why it is worth declaring at all: ' +
              `four seekers are one declaration, not four. ${EDITOR_HINT}`
            }
          />
        </Box>
        <Tooltip title={refusal} describeChild>
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              disabled={Boolean(refusal)}
              onClick={() => setAdding(true)}
            >
              Add child part
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {detail.contains.length === 0 ? (
        // A BOX WHERE THE TABLE WOULD BE, rather than a line of grey text. An
        // empty section that renders as a sentence reads as a section that
        // failed to load - which is how BALROG's system attributes were taken
        // for a missing panel - and the outline says "this belongs here and is
        // empty". The Add button above stays enabled either way: nothing to
        // list is the normal state of a part somebody is about to fill in.
        <Paper
          variant="outlined"
          sx={{ p: 2, borderStyle: 'dashed', textAlign: 'center' }}
        >
          <Typography variant="body2" color="text.secondary">
            {detail.is_definition
              ? NO_CHILDREN
              : // A usage is the one case worth a sentence: it is empty for a
                // REASON, and without it an obviously-composite part reads as
                // broken.
                'Nothing of its own — what it is made of is declared on the definition ' +
                'it is typed by.'}
          </Typography>
        </Paper>
      ) : (
        // FOUR COLUMNS IN A NARROW PANE. This sits in a two-fifths column with
        // a qualified type name in it, so it overflowed the pane rather than
        // its own box - the last column ended up past the edge. Scrolling
        // inside the container keeps it whole; shrinking the columns to fit
        // would truncate a type name, which is the one thing here nobody can
        // reconstruct from what is left.
        <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" aria-label="Parts inside this one">
          <TableHead>
            {/* Headings do not wrap: 'A kind of' broke across three lines and
                made the header three rows tall for one word per line. */}
            <TableRow sx={{ '& th': { whiteSpace: 'nowrap' } }}>
              <TableCell>Part</TableCell>
              <TableCell>Multiplicity</TableCell>
              <TableCell>Written as</TableCell>
              {/* 'A kind of' read as a sentence fragment continuing the row and
                  wrapped to three lines in the space it had. This column is the
                  DEFINITION the usage resolves to - the link that makes the
                  design walkable - so it is named that. */}
              <TableCell>Definition</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {detail.contains.map((child) => (
              <TableRow key={child.qualified_name || child.id}>
                <TableCell>
                  {child.name}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', fontFamily: MONOSPACE }}
                  >
                    {child.id}
                  </Typography>
                </TableCell>
                <TableCell>
                  {child.multiplicity ? (
                    <Typography variant="body2" sx={{ fontFamily: MONOSPACE }}>
                      {child.multiplicity}
                    </Typography>
                  ) : (
                    <Tooltip
                      title="Empty means the language default, which is not the same as one."
                      describeChild
                    >
                      <Typography variant="body2" color="text.secondary">
                        default
                      </Typography>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell sx={{ fontFamily: MONOSPACE }}>{child.type_name}</TableCell>
                <TableCell>
                  {/* The resolved definition, which is the link that makes the
                      design walkable rather than searchable. */}
                  {child.definition ? (
                    <IdLink id={child.definition} />
                  ) : (
                    <Tooltip
                      title="Its type names nothing this program declares — most often a shared library definition, which is not this program’s to change."
                      describeChild
                    >
                      <Typography variant="body2" color="warning.main">
                        unresolved
                      </Typography>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </TableContainer>
      )}

      {/* EDITOR_HINT printed here, under every part. It is on the Child Parts
          heading's '?' now - a double-click opening the editor is worth knowing
          once, not on every selection. */}

      {/* Ports and the interfaces between them (issue #52). After the children,
          because an interface inside a definition joins ports ON those
          children - reading down the pane is reading outward from the part. */}
      <Divider />
      <PortsSection part={detail} refusal={refusal} onChanged={onChanged} />

      <Divider />
      <InterfacesSection part={detail} permission={permission} onChanged={onChanged} />

      {adding && (
        <AddChildDialog
          part={detail}
          onClose={() => setAdding(false)}
          onAdded={onChanged}
        />
      )}
    </Stack>
  )
}
